// src/components/common/DataTable.jsx
import React from 'react';
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Search, X } from 'lucide-react';

const DataTable = ({ 
  columns, 
  data = [], 
  onSort,
  sortConfig,
  currentPage = 0,  
  pageSize = 10,
  totalElements = 0,
  onPageChange,
  onPageSizeChange,
  isLoading = false,
  disabled = false,
  // New props for enhancements
  onSearch,
  searchValue = '',
  searchPlaceholder = 'Cari...',
  enableQuickJump = false,
  customPageSizes = [10, 25, 50, 100],
  striped = false,
  compact = false,
  stickyHeader = false,
  maxHeight = null,
  // Theme customization
  headerBgColor = 'bg-gradient-to-r from-red-600 to-red-500',
  headerTextColor = 'text-white',
  hoverColor = 'hover:bg-red-50'
}) => {
  // Hitung total pages
  const totalPages = Math.ceil(totalElements / pageSize);
  
  // Hitung range data yang ditampilkan
  const startIndex = (currentPage * pageSize) + 1;
  const endIndex = Math.min((currentPage + 1) * pageSize, totalElements);

  // Handle sort click
  const handleSortClick = (key) => {
    if (!onSort || !sortConfig) return;
    
    const direction = sortConfig.sortBy === key && sortConfig.direction === 'ASC' ? 'DESC' : 'ASC';
    onSort({ sortBy: key, direction });
  };

  // Render sort indicator
  const renderSortIndicator = (column) => {
    if (!sortConfig || !column.sortable) return null;
    
    if (sortConfig.sortBy !== column.key) {
      return <span className="text-gray-400 opacity-50">↕</span>;
    }
    
    return sortConfig.direction === 'ASC' ? 
      <span className="text-white font-bold">↑</span> : 
      <span className="text-white font-bold">↓</span>;
  };

  // Generate page numbers for quick jump
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 0; i < totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(0);
      
      // Calculate range around current page
      let start = Math.max(1, currentPage - 1);
      let end = Math.min(totalPages - 2, currentPage + 1);
      
      // Add ellipsis if needed
      if (start > 1) pages.push('...');
      
      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      // Add ellipsis if needed
      if (end < totalPages - 2) pages.push('...');
      
      // Always show last page
      pages.push(totalPages - 1);
    }
    
    return pages;
  };

  return (
    <div className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
      {/* Search Bar (if enabled) */}
      {onSearch && (
        <div className="p-4 border-b border-gray-200 bg-gray-50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              value={searchValue}
              onChange={(e) => onSearch(e.target.value)}
              placeholder={searchPlaceholder}
              className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
              disabled={disabled}
            />
            {searchValue && (
              <button
                onClick={() => onSearch('')}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Table Container */}
      <div 
        className={`overflow-x-auto ${stickyHeader ? 'overflow-y-auto' : ''}`}
        style={maxHeight ? { maxHeight } : {}}
      >
        <table className="min-w-full divide-y divide-gray-200">
          {/* Table Header */}
          <thead className={`${headerBgColor} ${headerTextColor} ${stickyHeader ? 'sticky top-0 z-10' : ''}`}>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className={`px-6 py-3 text-left text-xs font-semibold uppercase tracking-wider ${
                    column.sortable ? 'cursor-pointer select-none hover:bg-opacity-90' : ''
                  } ${column.width ? column.width : ''}`}
                  onClick={() => column.sortable && !disabled && handleSortClick(column.key)}
                  style={column.minWidth ? { minWidth: column.minWidth } : {}}
                >
                  <div className="flex items-center space-x-1">
                    <span>{column.label}</span>
                    {renderSortIndicator(column)}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody className={`bg-white divide-y divide-gray-200 ${compact ? 'text-sm' : ''}`}>
            {isLoading ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center space-y-3">
                    <div className="flex space-x-2">
                      <div className="w-3 h-3 bg-red-400 rounded-full animate-bounce"></div>
                      <div className="w-3 h-3 bg-red-500 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                      <div className="w-3 h-3 bg-red-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                    </div>
                    <p className="text-gray-500 text-sm">Memuat data...</p>
                  </div>
                </td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center justify-center space-y-2">
                    <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
                    </svg>
                    <p className="text-gray-500 text-sm font-medium">Tidak ada data</p>
                    <p className="text-gray-400 text-xs">Data akan muncul di sini</p>
                  </div>
                </td>
              </tr>
            ) : (
              data.map((row, index) => (
                <tr 
                  key={row.id || index} 
                  className={`
                    ${hoverColor} transition-colors
                    ${striped && index % 2 === 1 ? 'bg-gray-50' : ''}
                  `}
                >
                  {columns.map((column) => (
                    <td 
                      key={column.key} 
                      className={`px-6 ${compact ? 'py-2' : 'py-4'} ${column.nowrap !== false ? 'whitespace-nowrap' : ''}`}
                    >
                      {column.render ? column.render(row, index) : row[column.key] || '-'}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Section */}
      {totalElements > 0 && (
        <div className="bg-gray-50 px-4 py-3 border-t border-gray-200">
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
            {/* Data Range Info */}
            <div className="text-sm text-gray-700">
              Menampilkan <span className="font-semibold text-red-600">{startIndex}-{endIndex}</span> dari{' '}
              <span className="font-semibold text-red-600">{totalElements}</span> data
            </div>

            {/* Right Section: Page Size & Navigation */}
            <div className="flex flex-col sm:flex-row items-center space-y-3 sm:space-y-0 sm:space-x-4">
              {/* Page Size Selector */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-700">Tampilkan</span>
                <select
                  value={pageSize}
                  onChange={(e) => onPageSizeChange(Number(e.target.value))}
                  className="border border-gray-300 rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  disabled={disabled}
                >
                  {customPageSizes.map(size => (
                    <option key={size} value={size}>{size}</option>
                  ))}
                </select>
                <span className="text-sm text-gray-700">data</span>
              </div>

              {/* Pagination Controls */}
              <div className="flex items-center space-x-2">
                {enableQuickJump && (
                  <>
                    {/* First Page */}
                    <button
                      onClick={() => onPageChange(0)}
                      disabled={disabled || currentPage === 0}
                      className="p-2 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Halaman Pertama"
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </button>
                  </>
                )}

                {/* Previous Page */}
                <button
                  onClick={() => onPageChange(currentPage - 1)}
                  disabled={disabled || currentPage === 0}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Halaman Sebelumnya"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {/* Page Numbers (if enableQuickJump) */}
                {enableQuickJump && (
                  <div className="hidden md:flex items-center space-x-1">
                    {getPageNumbers().map((page, idx) => (
                      page === '...' ? (
                        <span key={`ellipsis-${idx}`} className="px-3 py-1 text-gray-500">
                          ...
                        </span>
                      ) : (
                        <button
                          key={page}
                          onClick={() => onPageChange(page)}
                          disabled={disabled}
                          className={`px-3 py-1 border rounded-md text-sm transition-colors ${
                            currentPage === page
                              ? 'bg-red-600 text-white border-red-600 font-semibold'
                              : 'border-gray-300 hover:bg-gray-100'
                          } disabled:opacity-50 disabled:cursor-not-allowed`}
                        >
                          {page + 1}
                        </button>
                      )
                    ))}
                  </div>
                )}

                {/* Current Page Indicator (if not enableQuickJump) */}
                {!enableQuickJump && (
                  <span className="px-3 py-1 text-sm text-gray-700">
                    <span className="font-semibold text-red-600">{currentPage + 1}</span>
                    {' / '}
                    <span className="font-semibold">{totalPages}</span>
                  </span>
                )}

                {/* Next Page */}
                <button
                  onClick={() => onPageChange(currentPage + 1)}
                  disabled={disabled || currentPage >= totalPages - 1}
                  className="p-2 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Halaman Selanjutnya"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>

                {enableQuickJump && (
                  <>
                    {/* Last Page */}
                    <button
                      onClick={() => onPageChange(totalPages - 1)}
                      disabled={disabled || currentPage >= totalPages - 1}
                      className="p-2 border border-gray-300 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                      title="Halaman Terakhir"
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DataTable;